import type { FetchCurrentUserByTenantId } from '../../api/types/user.types';

import NetworkError from '../../api/http/NetworkError';
import { fetchCurrentUser } from '../../api';
import { useMutation } from '../base';

export default function useLoggedUser() {
  const { data, isPending, mutateAsync } = useMutation({
    mutationFn: ({ afterMutation, token }: FetchCurrentUserByTenantId) =>
      fetchCurrentUser({
        afterMutation,
        token: token || '',
      }),
    // Sentry 6Q. `AuthProvider` owns this mutation's failures: its boot listener reports
    // them and decides what the app does next. A dropped transport it deliberately stays
    // quiet about — the page was hidden, the session is fine, and the retry on the way back
    // will answer — must not then arrive through the generic route as a red
    // "Error, no response from server". Everything the backend actually ANSWERED keeps its
    // toast: that is news, and the user can act on it.
    //
    // Scoped to the listener's renewal (`afterMutation: false`) alone. `refetchCurrentUser`
    // runs this same mutation right after the user changed something, and its inner catch
    // resolves `undefined` — so a dropped `/auth/me` there leaves `currentUser` stale while
    // the flow reports success, and this toast is the only signal the refresh failed.
    // Anything but the expected variables shape fails open, toward the toast.
    meta: {
      suppressErrorToast: (error: unknown, variables: unknown) =>
        error instanceof NetworkError &&
        typeof variables === 'object' &&
        variables !== null &&
        'afterMutation' in variables &&
        variables.afterMutation === false,
    },
  });

  return {
    data,
    isPending,
    mutateAsync,
  };
}
